import { Box, InputBase, IconButton, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useFiltersStore } from '../../../store/filtersStore';
import { useEstadosStore } from '../../../store/estadosStore';
import { capitalizeFirstLetter } from '../../../utils/stringUtils';

interface SearchBarProps {
  onSearch?: (value: string) => void;
  value?: string;
}

const getFilterLabel = (filter: string | number, estadosMPInverso: Record<string, string>) => {
  if (filter === 'others') return 'Analizando';
  if (filter === 2) return 'Pendientes';
  if (filter === 3) return 'Seguidas';
  if (filter === 4) return 'Descartadas';
  if (typeof filter === 'string' && filter.startsWith('codigo_estado_')) {
    const codigo = filter.replace('codigo_estado_', '');
    return capitalizeFirstLetter(estadosMPInverso[codigo] || filter);
  }
  return filter.toString();
};

const getFilterLabelForType = (filter: { type: string; value: string | number }, estadosMPInverso: Record<string, string>) => {
  if (filter.type === 'postulado') return 'Postulado';
  if (filter.type === 'adjudicado') return 'Adjudicado';
  if (filter.type === 'no-leidas') return 'No Leídas';
  return getFilterLabel(filter.value, estadosMPInverso);
};

const FilterTag = ({ 
  label, 
  onRemove, 
  isMP 
}: { 
  label: string; 
  onRemove: () => void; 
  isMP: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      height: 24,
      backgroundColor: isMP ? '#F5F5F5' : '#E3F2FD',
      borderRadius: '12px',
      padding: '0 8px',
      '&:hover': {
        backgroundColor: isMP ? '#EEEEEE' : '#D6EAFC',
      }
    }}
  >
    <Typography
      sx={{
        fontSize: '12px',
        color: '#666',
        userSelect: 'none'
      }}
    >
      {label}
    </Typography>
    <Box
      onClick={onRemove}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '2px',
        borderRadius: '50%',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <CloseIcon sx={{ fontSize: 14, color: '#666' }} />
    </Box>
  </Box>
);

export const SearchBar: React.FC<SearchBarProps> = ({ onSearch, value = '' }) => {
  const { activeStatusFilters, toggleStatusFilter, postuladoFilter, adjudicadoFilter, noLeidasFilter, setPostuladoFilter, setAdjudicadoFilter, setNoLeidasFilter } = useFiltersStore();
  const { estadosMPInverso } = useEstadosStore();

  const allActiveFilters = [
    ...activeStatusFilters.map(filter => ({ type: 'status', value: filter })),
    ...(postuladoFilter === true ? [{ type: 'postulado', value: 'postulado' }] : []),
    ...(adjudicadoFilter === true ? [{ type: 'adjudicado', value: 'adjudicado' }] : []),
    ...(noLeidasFilter === true ? [{ type: 'no-leidas', value: 'no-leidas' }] : [])
  ];

  const visibleFilters = allActiveFilters.slice(0, 3);
  const remainingFilters = allActiveFilters.length - 3;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '980px',
        width: '100%',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          border: '1px solid #E6E6E6',
          borderRadius: 1,
          padding: '4px 40px 4px 10px',
          gap: 1,
          minHeight: 38,
          '&:focus-within': {
            borderColor: '#999',
          },
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1,
            flexWrap: 'nowrap',
            overflow: 'hidden'
          }}
        >
          {visibleFilters.map((filter) => (
            <FilterTag
              key={`${filter.type}-${filter.value}`}
              label={getFilterLabelForType(filter, estadosMPInverso)}
              onRemove={() => {
                if (filter.type === 'status') {
                  toggleStatusFilter(filter.value);
                } else if (filter.type === 'postulado') {
                  setPostuladoFilter(null);
                } else if (filter.type === 'adjudicado') {
                  setAdjudicadoFilter(null);
                } else if (filter.type === 'no-leidas') {
                  setNoLeidasFilter(null);
                }
              }}
              isMP={filter.type === 'status' && typeof filter.value === 'string' && filter.value.startsWith('codigo_estado_')}
            />
          ))}
          {remainingFilters > 0 && (
            <Tooltip title={allActiveFilters.slice(3).map(f => getFilterLabelForType(f, estadosMPInverso)).join(', ')}>
              <Box
                sx={{
                  height: 24,
                  backgroundColor: '#FAFAFA',
                  borderRadius: '12px',
                  padding: '0 8px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography sx={{ fontSize: '12px', color: '#666' }}>
                  +{remainingFilters}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
        <InputBase
          placeholder="Buscar..."
          value={value}
          fullWidth
          sx={{
            fontSize: '14px',
            flex: 1,
            '& input': {
              padding: '0'
            }
          }}
          onChange={(e) => onSearch?.(e.target.value)}
        />
      </Box>
      <IconButton
        sx={{
          width: 36,
          height: 36,
          position: 'absolute',
          right: 0,
          bgcolor: '#B4B4B4',
          borderRadius: '0 5px 5px 0',
          '&:hover': {
            bgcolor: '#A4A4A4',
          },
          '& svg': {
            fontSize: '18px',
            color: '#FFFFFF',         
          }
        }}
      >
        <SearchIcon />
      </IconButton>
    </Box>
  );
}; 